import { useCallback } from 'react'
import type { BlockGrid, BlockSlot } from '@/core/time/blockGrid'
import type { ChangeSet, EntryContent } from '@/core/repository/entryRepository'
import { erase } from '@/core/repository/entryRepository'
import {
  advanceCursor,
  eraseSelection,
  markSelection,
  type Selection,
} from '@/core/services/markingService'
import { copySelection, cutSelection, pasteAt } from '@/core/services/clipboardService'
import { playSound } from '@/core/platform/sound'
import { applyRedo, applyUndo } from '@/core/services/undoService'
import { logger } from '@/core/logging/logger'
import { useTimelineStore } from './useTimelineStore'

/**
 * Every editing action, in one place.
 *
 * Each records its ChangeSet so undo can invert it, and each schedules a sync.
 * Keeping them together is what stops one path forgetting the other -- an
 * operation that is undoable but never syncs, or vice versa, is a bug you only
 * notice much later.
 */
export function useTimelineActions(
  grid: BlockGrid,
  options: {
    cursorMode: boolean
    skipOccupied: boolean
    /** Mark animation and optional tone. Both are feedback, so they fire together. */
    soundEnabled: boolean
    /** An event type may carry a default object, attached automatically. */
    defaultObjectFor?: (eventTypeId: string) => string | null
    onChanged: () => void
  },
) {
  const selection = useTimelineStore((s) => s.selection)
  const sticky = useTimelineStore((s) => s.stickySelection)
  const clipboard = useTimelineStore((s) => s.clipboard)
  const setSelection = useTimelineStore((s) => s.setSelection)
  const setClipboard = useTimelineStore((s) => s.setClipboard)
  const recordChange = useTimelineStore((s) => s.recordChange)
  const popUndo = useTimelineStore((s) => s.popUndo)
  const popRedo = useTimelineStore((s) => s.popRedo)

  const { cursorMode, skipOccupied, soundEnabled, defaultObjectFor, onChanged } = options
  const flashMarked = useTimelineStore((s) => s.flashMarked)

  /**
   * Feedback for a completed write.
   *
   * Driven by the ChangeSet rather than by the selection, so it lights up
   * exactly the blocks that actually changed -- skip-occupied means those are
   * not always the same thing.
   */
  const feedback = useCallback(
    (change: ChangeSet, kind: 'mark' | 'erase') => {
      flashMarked(change.after.map((row) => `${row.dayKey}:${row.slotIndex}`))
      if (soundEnabled && change.after.length > 0) playSound(kind)
    },
    [flashMarked, soundEnabled],
  )

  const after = useCallback(
    (change: ChangeSet) => {
      recordChange(change)
      onChanged()
    },
    [recordChange, onChanged],
  )

  const mark = useCallback(
    (eventTypeId: string) => {
      if (!selection) return
      const content: EntryContent = {
        eventTypeId,
        // Attaching the type's default object here is what makes the second
        // dimension usable: you pick "Coding" and the project comes with it.
        eventObjectId: defaultObjectFor?.(eventTypeId) ?? null,
        comment: null,
        isPlanned: false,
      }

      void markSelection(grid, selection, content, { skipOccupied })
        .then((change) => {
          after(change)
          feedback(change, 'mark')
          // Sticky mode keeps the range so it can be re-marked; otherwise the
          // cursor steps on, which is what makes a day fillable in seconds.
          if (!sticky && cursorMode) setSelection(advanceCursor(grid, selection))
        })
        .catch((err: unknown) => logger.error('db', 'Marking failed', err))
    },
    [selection, grid, skipOccupied, sticky, cursorMode, setSelection, after, feedback, defaultObjectFor],
  )

  /**
   * Applies a whole saved combination in one go.
   *
   * Shares the marking path with `mark` rather than reimplementing it, so
   * cursor advance, sticky mode, skip-occupied, undo and sync all behave
   * identically — a stamper is only a pre-filled `EntryContent`.
   *
   * The stamper's own object wins over the type's default: choosing an object
   * when you saved the stamper is a more specific statement of intent than the
   * type-level default, and a stamper that quietly ignored it would be baffling.
   */
  const applyStamper = useCallback(
    (stamper: { eventTypeId: string | null; eventObjectId: string | null; comment: string | null }) => {
      if (!selection || !stamper.eventTypeId) return

      const content: EntryContent = {
        eventTypeId: stamper.eventTypeId,
        eventObjectId:
          stamper.eventObjectId ?? defaultObjectFor?.(stamper.eventTypeId) ?? null,
        comment: stamper.comment,
        isPlanned: false,
      }

      void markSelection(grid, selection, content, { skipOccupied })
        .then((change) => {
          after(change)
          feedback(change, 'mark')
          if (!sticky && cursorMode) setSelection(advanceCursor(grid, selection))
        })
        .catch((err: unknown) => logger.error('db', 'Applying a stamper failed', err))
    },
    [selection, grid, skipOccupied, sticky, cursorMode, setSelection, after, feedback, defaultObjectFor],
  )

  const eraseSelected = useCallback(() => {
    if (!selection) return
    void eraseSelection(grid, selection)
      .then((change) => {
        after(change)
        // `before`, not `after`: an erase produces tombstones, so the rows
        // that changed on screen are the ones that were there.
        flashMarked([])
        if (soundEnabled && change.before.length > 0) playSound('erase')
      })
      .catch((err: unknown) => logger.error('db', 'Erase failed', err))
  }, [selection, grid, after, flashMarked, soundEnabled])

  const eraseDay = useCallback(
    (dayKey: number) => {
      const slots: BlockSlot[] = Array.from({ length: grid.slotsPerDay(dayKey) }, (_, i) => ({
        dayKey,
        slotIndex: i,
      }))
      void erase(slots)
        .then(after)
        .catch((err: unknown) => logger.error('db', 'Erase day failed', err))
    },
    [grid, after],
  )

  const copy = useCallback(() => {
    if (!selection) return
    void copySelection(grid, selection)
      .then(setClipboard)
      .catch((err: unknown) => logger.error('db', 'Copy failed', err))
  }, [selection, grid, setClipboard])

  const cut = useCallback(() => {
    if (!selection) return
    void cutSelection(grid, selection)
      .then(({ clipboard: clip, change }) => {
        setClipboard(clip)
        after(change)
      })
      .catch((err: unknown) => logger.error('db', 'Cut failed', err))
  }, [selection, grid, setClipboard, after])

  const paste = useCallback(
    (anchor?: BlockSlot) => {
      const target = anchor ?? selection?.anchor
      if (!clipboard || !target) return

      void pasteAt(grid, clipboard, target)
        .then(after)
        .catch((err: unknown) => logger.error('db', 'Paste failed', err))
    },
    [clipboard, selection, grid, after],
  )

  const undo = useCallback(() => {
    const change = popUndo()
    if (!change) return
    void applyUndo(change)
      .then(onChanged)
      .catch((err: unknown) => logger.error('db', 'Undo failed', err))
  }, [popUndo, onChanged])

  const redo = useCallback(() => {
    const change = popRedo()
    if (!change) return
    void applyRedo(change)
      .then(onChanged)
      .catch((err: unknown) => logger.error('db', 'Redo failed', err))
  }, [popRedo, onChanged])

  return { mark, applyStamper, eraseSelected, eraseDay, copy, cut, paste, undo, redo }
}

export type TimelineActions = ReturnType<typeof useTimelineActions>
export type { Selection }
